import React, { useCallback, useEffect, useState } from 'react';
import { Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import { CloseRounded, CloudUploadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';

export default function Dropzone({ children, setFieldValue, uploadedFiles = [], error, helperText, type, accept, maxSize, multiple = false, maxFiles, onDropRejected }) {
  const [myFiles, setMyFiles] = useState([]);

  useEffect(() => {
    const selectedFiles = Array.isArray(uploadedFiles)
      ? uploadedFiles.filter((file) => typeof File !== 'undefined' && file instanceof File)
      : [];
    setMyFiles(selectedFiles);
  }, [uploadedFiles]);

  const onDrop = useCallback((acceptedFiles) => {
    const nextFiles = multiple ? [...myFiles, ...acceptedFiles] : [...acceptedFiles];
    setMyFiles(nextFiles);
    setFieldValue(nextFiles);
  }, [multiple, myFiles, setFieldValue]);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: accept || (type === true ? { 'application/csv': ['.Custom', '.csv'] } : ''),
    onDrop,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
    maxSize,
    maxFiles,
    multiple,
  });

  const removeFile = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    const newFiles = [...myFiles];
    newFiles.splice(index, 1);
    setMyFiles(newFiles);
    setFieldValue(newFiles);
  };

  const existingFiles = Array.isArray(uploadedFiles) ? uploadedFiles : [];

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '1.5px dashed',
          borderColor: error ? '#ef4444' : isDragActive ? '#1769e8' : '#b9c8dc',
          borderRadius: '12px',
          bgcolor: isDragActive ? '#eef5ff' : '#f8fbff',
          overflow: 'hidden',
          transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
          '&:hover': { borderColor: error ? '#ef4444' : '#1769e8', bgcolor: '#f3f8ff', boxShadow: '0 6px 18px rgba(23, 105, 232, .07)' },
        }}
      >
        <input {...getInputProps()} />
        <ButtonBase type="button" onClick={open} sx={{ width: '100%', minHeight: 150, px: 2, py: 2.2, borderRadius: '12px' }}>
          <Stack alignItems="center" spacing={0.75}>
            <Box sx={{ position: 'relative', width: 46, height: 46, display: 'grid', placeItems: 'center', overflow: 'hidden', borderRadius: '12px', bgcolor: '#e7f0ff', color: '#1769e8' }}>
              {children && <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', opacity: 0.1, pointerEvents: 'none', '& .image-wrapper': { width: 38, height: 38, borderRadius: 0 }, '& img': { width: '38px !important', height: '38px !important', objectFit: 'contain' } }}>{children}</Box>}
              <CloudUploadOutlined sx={{ position: 'relative', zIndex: 1, fontSize: 25 }} />
            </Box>
            <Typography sx={{ color: '#172b4d', fontSize: 13, fontWeight: 750 }}>
              {isDragActive ? 'Drop the file here' : 'Choose a file or drag it here'}
            </Typography>
            <Typography sx={{ color: '#718096', fontSize: 11 }}>{type === true ? 'CSV files only' : multiple ? 'Upload supported files' : 'Upload one supported file'}</Typography>
          </Stack>
        </ButtonBase>
      </Box>

      {(myFiles.length > 0 || existingFiles.length > 0) && (
        <Stack spacing={0.75} sx={{ mt: 1.1 }}>
          {myFiles.map((file, index) => (
            <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.2, py: 0.8, border: '1px solid #e2e8f0', borderRadius: '9px', bgcolor: '#fff' }}>
              <InsertDriveFileOutlined sx={{ color: '#1769e8', fontSize: 20 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ color: '#243858', fontSize: 11.5, fontWeight: 650 }}>{file.name || file.path}</Typography>
                <Typography sx={{ color: '#8491a6', fontSize: 10 }}>{Math.max(1, Math.round(file.size / 1024))} KB</Typography>
              </Box>
              <IconButton aria-label={`Remove ${file.name || 'file'}`} size="small" onClick={(event) => removeFile(event, index)} sx={{ color: '#e5484d', bgcolor: '#fff1f2', '&:hover': { bgcolor: '#ffe4e6' } }}>
                <CloseRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}

          {!myFiles.length && existingFiles.map((file, index) => {
            const fileUrl = file?.nft_documents;
            return (
              <Box key={fileUrl || index} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.2, py: 1, border: '1px solid #e2e8f0', borderRadius: '9px', bgcolor: '#fff' }}>
                <InsertDriveFileOutlined sx={{ color: '#1769e8', fontSize: 20 }} />
                <Typography component={fileUrl ? Link : 'span'} to={fileUrl || undefined} download noWrap sx={{ minWidth: 0, color: '#243858', fontSize: 11.5, fontWeight: 650 }}>
                  {fileUrl?.split(/(\\|\/)/g).pop() || file?.name || 'Uploaded file'}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      {error && <Typography className="Mui-error" sx={{ mt: 0.7, ml: 0.5, color: '#e5484d', fontSize: 11 }}>{helperText}</Typography>}
    </Box>
  );
}
